
import { DashboardLayout } from "@/components/dashboard/layout";
import { useCallback, useState } from "react";
import { DollarSign, ShoppingCart, TrendingUp, ChartLine } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { DateRangePicker } from "@/components/DateRangePicker";
import { DateRange } from "react-day-picker";
import { subDays } from "date-fns";
import {
  fetchRevenueData,
  fetchCoursePerformance,
  fetchSalesStats,
} from "@/services/salesService";
import { fetchTransactions, exportTransactionsCSV } from "@/services/recentTransactionsService";
import { ErrorDisplay } from "@/components/public-course/ErrorDisplay";
import { devLog } from "@/utils/devLog";
import { Button } from "@/components/ui/button";
import { KpiCard } from "@/components/sales/KpiCard";
import { RevenueChart } from "@/components/sales/RevenueChart";
import { CoursePerformanceChart } from "@/components/sales/CoursePerformanceChart";
import { TransactionsCard } from "@/components/sales/TransactionsCard";
import { useToast } from "@/components/ui/use-toast";

export default function Sales() {
  const { toast } = useToast();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 30),
    to: new Date(),
  });
  
  const [timeInterval, setTimeInterval] = useState<"daily" | "weekly" | "monthly">("daily");
  const [courseFilter, setCourseFilter] = useState("revenue");
  
  const handleDateRangeChange = useCallback((range: DateRange | undefined) => {
    if (range) {
      devLog("[Sales] Date range changed:", range);
      setDateRange(range);
    }
  }, []);

  const { data: salesStats, isLoading: isLoadingStats, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['salesStats', dateRange],
    queryFn: () => fetchSalesStats(dateRange),
    retry: 3
  });

  const { data: revenueData, isLoading: isLoadingRevenue, error: revenueError, refetch: refetchRevenue } = useQuery({
    queryKey: ['revenueData', dateRange, timeInterval],
    queryFn: () => fetchRevenueData(dateRange, timeInterval),
    retry: 3
  });

  const { data: courseData, isLoading: isLoadingCourses, error: coursesError, refetch: refetchCourses } = useQuery({
    queryKey: ['coursePerformance', dateRange],
    queryFn: () => fetchCoursePerformance(dateRange),
    retry: 3
  });

  const { data: transactions, isLoading: isLoadingTransactions, error: transactionsError, refetch: refetchTransactions } = useQuery({
    queryKey: ['transactions', dateRange],
    queryFn: () => fetchTransactions(dateRange),
    retry: 3
  });

  const handleCourseFilterChange = useCallback((value: string) => {
    setCourseFilter(value);
  }, []);

  const handleExportCSV = useCallback(() => {
    if (!transactions || transactions.length === 0) {
      toast({
        title: "No data to export",
        description: "There are no transactions in the selected period.",
        variant: "destructive"
      });
      return;
    }
    
    exportTransactionsCSV(transactions);
  }, [transactions, toast]);
  
  const handleRetryCourses = useCallback(() => {
    devLog("[Sales] Retrying course performance data fetch");
    refetchCourses();
  }, [refetchCourses]);

  const handleRetryTransactions = useCallback(() => {
    devLog("[Sales] Retrying transactions data fetch");
    refetchTransactions();
  }, [refetchTransactions]);

  return (
    <DashboardLayout 
      title="Sales Analytics" 
      description="Track revenue, course sales, and financial metrics"
      actions={
        <DateRangePicker 
          dateRange={dateRange} 
          setDateRange={handleDateRangeChange} 
        />
      }
    >
      <div className="space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard 
            title="Total Revenue" 
            value={`$${salesStats?.totalRevenue.toLocaleString() ?? 0}`} 
            change={0}
            icon={<DollarSign className="h-5 w-5 text-primary" />} 
            isLoading={isLoadingStats}
          />
          <KpiCard 
            title="Period Revenue" 
            value={`$${salesStats?.periodRevenue.toLocaleString() ?? 0}`} 
            change={salesStats?.periodChange ?? 0} 
            icon={<ChartLine className="h-5 w-5 text-primary" />} 
            isLoading={isLoadingStats}
          />
          <KpiCard 
            title="Courses Sold" 
            value={salesStats?.coursesSold?.toString() ?? "0"} 
            change={salesStats?.periodChange ?? 0} 
            icon={<ShoppingCart className="h-5 w-5 text-primary" />} 
            isLoading={isLoadingStats}
          />
          <KpiCard 
            title="Avg. Order Value" 
            value={`$${salesStats?.avgOrderValue?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? 0}`} 
            change={0} 
            icon={<TrendingUp className="h-5 w-5 text-primary" />} 
            isLoading={isLoadingStats}
          />
        </div>
        
        {revenueError ? (
          <ErrorDisplay 
            error={revenueError as Error} 
            onRetry={() => refetchRevenue()} 
          />
        ) : (
          <RevenueChart 
            data={revenueData ?? []} 
            isLoading={isLoadingRevenue}
            timeInterval={timeInterval}
            onIntervalChange={setTimeInterval}
          />
        )}

        {coursesError ? (
          <ErrorDisplay 
            error={coursesError as Error} 
            onRetry={handleRetryCourses} 
          />
        ) : (
          <CoursePerformanceChart 
            data={courseData ?? []}
            isLoading={isLoadingCourses}
            filter={courseFilter}
            onFilterChange={handleCourseFilterChange}
          />
        )}
        
        {transactionsError ? (
          <ErrorDisplay 
            error={transactionsError as Error} 
            onRetry={handleRetryTransactions} 
          />
        ) : (
          <TransactionsCard 
            data={transactions ?? []}
            isLoading={isLoadingTransactions}
            onExportCSV={handleExportCSV}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
